import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Camera, Coins, LayoutGrid, ScrollText, Warehouse } from 'lucide-react';
import { DEFAULT_B2B_RANGE_ID, DEFAULT_B2C_RANGE_ID } from '../../constants/prices';

import { SITES_UPDATED_SUBSCRIPTION } from 'data/subscriptions/site';
import {
    SiteContractType,
    SiteDeliveryType,
    SiteState,
    SiteType,
    useGetProductPriceRangesQuery,
    useGetSiteQuery,
    useUpdateSiteMutation,
    useGetDeliveryZonesQuery,
    useAssignSitesToDeliveryZoneMutation,
} from 'data/__generated__';

import { Loader } from 'components/Loader';
import { TotemSelect } from 'components/TotemSelect';
import { ColumnsSectionContainer, SectionContainer } from 'components/DetailsView/Section';
import {
    ActionsAndSave,
    EntityPageBreadcrumb,
    PageActions,
    PageHeader,
    type PageActionGroup,
} from 'components/PageHeader';
import { TotemLabel } from 'components/TotemLabel';

import { UrbantzMetadataForm } from './components/UrbantzMetadataForm';
import { ReassortDaysSection } from './SiteDetails/ReassortDaysSection';
import { GeneralInfoSection } from 'pages/Sites/SiteDetails/GeneralInfoSection';
import { DeliveryInfoSection } from 'pages/Sites/SiteDetails/DeliveryInfoSection';
import { SiteDeliveryPlanningSection } from 'pages/Sites/SiteDetails/SiteDeliveryPlanningSection';
import { MicrostoreColumnsSection } from 'pages/Sites/SiteDetails/MicrostoreColumnsSection';
import { WAREHOUSE_SITE_TO_COLUMN_ID } from 'pages/Stocks/constants';

import { SiteRituals } from './components/SiteRituals';
import { siteUpdatedHandler } from './utils/sitesUpdatedHandler';
import { apolloClient } from '../../App';
import {
    getNumericValue,
    stockTargetRatiosCheck,
} from 'pages/MicrostoreColumnGroups/MicrostoreColumnGroupDetails/utils';
import { SiteWantedProducts } from './components/SiteWantedProducts';
import { ConsumptionVariations } from './ConsumptionVariation/ConsumptionVariations';
import { MenuSection } from './SiteDetails/MenuSection';
import { SiteTransactionsSection } from './SiteDetails/SiteTransactionsSection';
import { AuditLogs } from '../../components/AuditLogs';

type ParamTypes = {
    siteId: string;
};

type FormData = {
    accessTime: number;
    removalDate: string | null;
    B2CPriceRange: string | null;
    unknownShrinkageBillingThreshold: number | null;
    closingDatesRanges: { start: string | null; end: string | null }[];
    deliveryDays: boolean[];
    freshReassortDays: boolean[];
    dryReassortDays: boolean[];
    deliveryWindowStart: string;
    deliveryWindowStop: string;
    deliveryType: SiteDeliveryType;
    contractType: SiteContractType;
    estimatedUserCount: number;
    hasExternalSetupProcess: boolean;
    isOpenOnWeekend: boolean;
    isSecured: boolean;
    hasSensei: boolean;
    hasCoffee: boolean;
    launchDate: string | null;
    name: string;
    organizationId: string;
    reassortmentFreshFacings: {
        dessert: number;
        main: number;
        smallBites: number;
        starter: number;
    };
    reassortmentStockTargets: {
        dessert: number;
        main: number;
        smallBites: number;
        starter: number;
    };
    reassortmentOnboardingStockTargets: {
        dessert: number;
        main: number;
        smallBites: number;
        starter: number;
    };
    reassortmentStockTargetRatios: {
        monday: number;
        tuesday: number;
        wednesday: number;
        thursday: number;
        friday: number;
    };
    shouldOperateOnTotops: boolean;
    shouldAutomaticallyCreateTargetReassortment: boolean;
    shouldAutomaticallyCreateManualReassortment: boolean;
    shouldAutomaticallyFinishSiteSetup: boolean;
    shouldHaveTheftNotice: boolean;
    shouldModulateForExpired: boolean;
    shouldModulateForUnknownLosses: boolean;
    state: SiteState;
    useNayaxCard: boolean;
    unwantedProductIds: string[];
    urbantzMetadata: {
        needsCleaningMachineNECTA: Array<{ evenWeek: boolean; oddWeek: boolean }>;
        needsInventoryBoissons: Array<{ evenWeek: boolean; oddWeek: boolean }>;
        needsInventoryNonAlimentaire: Array<{ evenWeek: boolean; oddWeek: boolean }>;
        needsInventorySnack: Array<{ evenWeek: boolean; oddWeek: boolean }>;
        needsSmallExpiryDateInventory: Array<{ evenWeek: boolean; oddWeek: boolean }>;
        needsPunctualExpiryDateInventory: Array<{ evenWeek: boolean; oddWeek: boolean }>;
        replaceCompleteInventoryByZeroInventory: Array<{ evenWeek: boolean; oddWeek: boolean }>;
    };
    wantedProductIds: string[];
};

export const SiteDetails = () => {
    const { siteId = '' } = useParams<ParamTypes>();

    const {
        loading: siteLoading,
        data: siteData,
        error: siteError,
    } = useGetSiteQuery({
        variables: { siteId },
    });
    const {
        loading: priceRangesLoading,
        data: priceRangesData,
        error: priceRangesError,
    } = useGetProductPriceRangesQuery();
    const [updateSite, { loading: updateLoading }] = useUpdateSiteMutation();
    const { data: deliveryZonesData } = useGetDeliveryZonesQuery();
    const [assignSitesToDeliveryZone, { loading: assignZoneLoading }] = useAssignSitesToDeliveryZoneMutation();
    const [currentDeliveryZoneId, setCurrentDeliveryZoneId] = useState<string | null>(null);
    const [savedDeliveryZoneId, setSavedDeliveryZoneId] = useState<string | null>(null);

    const methods = useForm<FormData>({
        defaultValues: {
            urbantzMetadata: {
                needsCleaningMachineNECTA: [],
                needsInventoryBoissons: [],
                needsInventoryNonAlimentaire: [],
                needsInventorySnack: [],
                needsSmallExpiryDateInventory: [],
                needsPunctualExpiryDateInventory: [],
                replaceCompleteInventoryByZeroInventory: [],
            },
            closingDatesRanges: [],
            wantedProductIds: [],
            unwantedProductIds: [],
            freshReassortDays: [true, true, true, true, true, true, true],
            dryReassortDays: [true, true, true, true, true, true, true],
        },
    });
    const { handleSubmit, reset, watch, setValue } = methods;
    const formValues = watch();

    useEffect(() => {
        const observer = apolloClient.subscribe({
            query: SITES_UPDATED_SUBSCRIPTION,
            variables: { siteId },
        });
        const subscription = observer.subscribe(({ data }) => {
            const { siteUpdated } = data;
            siteUpdatedHandler(siteUpdated, siteId);
            reset({
                ...siteUpdated,
                deliveryWindowStart: siteUpdated.deliveryTimeWindow?.start,
                deliveryWindowStop: siteUpdated.deliveryTimeWindow?.stop,
            });
            toast.warn("Le site vient d'être mis à jour");
        });

        return () => subscription.unsubscribe();
    }, [reset]);

    useEffect(() => {
        if (siteData?.site) {
            const site = siteData.site;
            reset({
                ...site,
                deliveryWindowStart: site.deliveryTimeWindow?.start,
                deliveryWindowStop: site.deliveryTimeWindow?.stop,
            });
            setCurrentDeliveryZoneId(site.deliveryZoneId ?? null);
            setSavedDeliveryZoneId(site.deliveryZoneId ?? null);
        }
    }, [siteData, reset]);

    if (siteLoading || priceRangesLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (siteError || !siteData) {
        throw new Error('Une erreur est survenue lors de la récupération des sites');
    }

    if (priceRangesError || !priceRangesData) {
        throw new Error('Une erreur est survenue lors de la récupération des gammes');
    }

    const { productPriceRanges } = priceRangesData;
    const rangesWithoutDefault = productPriceRanges.filter(({ _id }) => DEFAULT_B2C_RANGE_ID !== _id);
    const rangeOptions = rangesWithoutDefault.map(({ _id, name }) => ({ label: name, value: _id }));

    if (formValues.B2CPriceRange === DEFAULT_B2B_RANGE_ID) {
        toast.warning(
            'ATTENTION : le prix B2B par défaut correspond au prix freefood et sera utilisé sur un store (ex Datadog)',
            {
                autoClose: false,
            },
        );
    }

    const onSubmit = handleSubmit(async (data) => {
        const stockTargetRatiosError = stockTargetRatiosCheck(data.reassortmentStockTargetRatios);
        if (stockTargetRatiosError) {
            toast.warning(stockTargetRatiosError);
            return;
        }

        const startHour = data.deliveryWindowStart.replace(':', '');
        const stopHour = data.deliveryWindowStop.replace(':', '');
        if (Number(startHour) >= Number(stopHour)) {
            toast.error(`Fenêtre de livraison invalide ${startHour} >= ${stopHour}`);
            return;
        }

        const { monday, tuesday, wednesday, thursday, friday } = data.reassortmentStockTargetRatios;

        // Vérifier s'il y a des dates de fermeture incomplètes
        const incompleteClosingDates = data.closingDatesRanges.filter(
            ({ start, end }) => start === null || end === null,
        );
        if (incompleteClosingDates.length > 0) {
            toast.error('Veuillez compléter toutes les dates de fermeture avant de sauvegarder');
            return;
        }

        const mutationVariables = {
            ...data,
            deliveryTimeWindow: {
                start: data.deliveryWindowStart,
                stop: data.deliveryWindowStop,
            },
            closingDatesRanges: data.closingDatesRanges.map(({ start, end }) => ({ start: start!, end: end! })),
            reassortmentStockTargetRatios: {
                monday: getNumericValue(monday),
                tuesday: getNumericValue(tuesday),
                wednesday: getNumericValue(wednesday),
                thursday: getNumericValue(thursday),
                friday: getNumericValue(friday),
            },
            urbantzMetadata: {
                needsCleaningMachineNECTA: data.urbantzMetadata.needsCleaningMachineNECTA.map(
                    ({ evenWeek, oddWeek }) => ({
                        evenWeek,
                        oddWeek,
                    }),
                ),
                needsInventoryBoissons: data.urbantzMetadata.needsInventoryBoissons.map(({ evenWeek, oddWeek }) => ({
                    evenWeek,
                    oddWeek,
                })),
                needsInventoryNonAlimentaire: data.urbantzMetadata.needsInventoryNonAlimentaire.map(
                    ({ evenWeek, oddWeek }) => ({
                        evenWeek,
                        oddWeek,
                    }),
                ),
                needsInventorySnack: data.urbantzMetadata.needsInventorySnack.map(({ evenWeek, oddWeek }) => ({
                    evenWeek,
                    oddWeek,
                })),
                needsSmallExpiryDateInventory: data.urbantzMetadata.needsSmallExpiryDateInventory.map(
                    ({ evenWeek, oddWeek }) => ({
                        evenWeek,
                        oddWeek,
                    }),
                ),
                needsPunctualExpiryDateInventory: data.urbantzMetadata.needsPunctualExpiryDateInventory.map(
                    ({ evenWeek, oddWeek }) => ({
                        evenWeek,
                        oddWeek,
                    }),
                ),
                replaceCompleteInventoryByZeroInventory:
                    data.urbantzMetadata.replaceCompleteInventoryByZeroInventory.map(({ evenWeek, oddWeek }) => ({
                        evenWeek,
                        oddWeek,
                    })),
            },
            siteId,
        };

        const { data: response } = await updateSite({
            variables: mutationVariables,
        });
        if (!response) {
            toast.error('Une erreur inconnue est survenue lors de la modification du site');
        } else if (!response.updateSite.success) {
            toast.error(`L'erreur suivante est survenue : "${response.updateSite.error}"`);
        } else {
            if (currentDeliveryZoneId !== savedDeliveryZoneId) {
                await assignSitesToDeliveryZone({
                    variables: { deliveryZoneId: currentDeliveryZoneId, siteIds: [siteId] },
                });
                setSavedDeliveryZoneId(currentDeliveryZoneId);
            }
            reset(data);
            toast.success('Le site a bien été modifié');
        }
    });

    const site = siteData.site;
    const totem = site.totem;
    const organization = totem.organization;

    const isMicrostore = site.type === SiteType.Microstore;

    // Warehouse sites map directly to a single stock column: jump straight there
    // instead of going through the site-columns listing.
    const warehouseColumnId = WAREHOUSE_SITE_TO_COLUMN_ID[siteId];
    const stocksTo = warehouseColumnId ? `/column/${warehouseColumnId}/stocks` : `/site/${siteId}/stocks`;

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Stocks',
            items: [
                { label: 'Consulter les stocks', icon: <Coins />, to: stocksTo },
                {
                    label: 'Inventaire externe',
                    icon: <Warehouse />,
                    to: `/site-known-sales-inventory`,
                },
            ],
        },
        ...(isMicrostore
            ? [
                  {
                      label: 'Microstore',
                      items: [
                          {
                              label: 'Éditeur legacy (planogramme)',
                              icon: <ScrollText />,
                              to: `/microstore-column-groups/${siteId}`,
                          },
                          { label: 'Planogramme visuel', icon: <LayoutGrid />, to: `/planogram/${siteId}` },
                          { label: 'Snapshot microstore', icon: <Camera />, to: `/microstore-snapshot/${siteId}` },
                      ],
                  },
              ]
            : []),
    ];

    return (
        <Container>
            <Helmet title={`${site.name} · TOTADMIN`} />
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <PageHeader>
                        <EntityPageBreadcrumb organization={organization} totem={totem} site={site} />
                        <PageActions>
                            <ActionsAndSave
                                actionGroups={actionGroups}
                                onSave={onSubmit}
                                saveLoading={updateLoading}
                                saveDataTest="submit-button"
                            />
                        </PageActions>
                    </PageHeader>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection siteDetails={siteData.site} />
                            {isMicrostore && (
                                <>
                                    <DeliveryInfoSection siteDetails={siteData.site} />
                                    <SiteDeliveryPlanningSection siteDetails={siteData.site} />
                                    <MicrostoreColumnsSection groups={site.microstoreColumnGroups} />
                                    <SectionContainer title="Zone de livraison" isInitiallyOpen>
                                        <TotemSelect
                                            label="Zone de livraison (appro référent)"
                                            placeholder="Sélectionner une zone"
                                            isClearable
                                            disabled={updateLoading || assignZoneLoading}
                                            value={currentDeliveryZoneId}
                                            options={(deliveryZonesData?.deliveryZones ?? []).map(({ _id, name }) => ({
                                                value: _id,
                                                label: name,
                                            }))}
                                            onChange={setCurrentDeliveryZoneId}
                                        />
                                    </SectionContainer>
                                    <SectionContainer title="Gamme de prix" isInitiallyOpen>
                                        <TotemSelect
                                            label="Gamme de prix"
                                            placeholder="Sélectionner une gamme de prix"
                                            isClearable
                                            value={formValues.B2CPriceRange ?? null}
                                            options={rangeOptions}
                                            onChange={(value) => {
                                                setValue('B2CPriceRange', value);
                                            }}
                                        />
                                    </SectionContainer>
                                    <SectionContainer title="Paramétrage réassort & rangement" isInitiallyOpen>
                                        <ColumnsSectionContainer numberOfColumns={1}>
                                            <TotemLabel>Réassort</TotemLabel>
                                            <ReassortDaysSection />
                                            <TotemLabel>Rangement</TotemLabel>
                                            <UrbantzMetadataForm />
                                        </ColumnsSectionContainer>
                                    </SectionContainer>
                                    <MenuSection siteDetails={siteData.site} />
                                    <SectionContainer title="Produits pour plano dry" isInitiallyOpen={false}>
                                        <SiteWantedProducts />
                                    </SectionContainer>
                                    <ConsumptionVariations siteId={siteId} />
                                    <SectionContainer title="Rituels du site" isInitiallyOpen={false}>
                                        <ColumnsSectionContainer numberOfColumns={1}>
                                            <SiteRituals siteId={siteId} />
                                        </ColumnsSectionContainer>
                                    </SectionContainer>
                                </>
                            )}
                            <SectionContainer
                                title="Transactions"
                                isInitiallyOpen={false}
                                // Liste paginée : sa requête ne part qu'à l'ouverture de la section.
                                lazy
                                linkToMore={`/transactions?siteId=${siteId}`}
                            >
                                <SiteTransactionsSection siteId={siteId} />
                            </SectionContainer>
                            <SectionContainer title="Audit logs" isInitiallyOpen={false}>
                                <AuditLogs collectionDocumentId={siteId} />
                            </SectionContainer>
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;
